// @flow
import { useDataLoader } from '../../logic/DataLoader'
import createPaymentIntent, {
  type PaymentIntentData,
} from 'mz-sdk/services/payment/createPaymentIntent'
import {
  createAuthorizedPaymentIntent,
  type StripeIntent,
  type StripeObject,
  type PaymentIntentAuthOptions,
} from './stripe'
import updatePaymentIntent from 'mz-sdk/services/payment/updatePaymentIntent'
import cancelPaymentIntent from 'mz-sdk/services/payment/cancelPaymentIntent'
import createSetupIntent from 'mz-sdk/services/payment/createSetupIntent'

export type Data = ?{
  ...PaymentIntentData,
  authorizedIntent: StripeIntent | null,
}

export type Actions = {
  setAuthorizedIntent: (intent: StripeIntent | null) => void,
  authorizePaymentIntent: (
    authArgs: $Shape<PaymentIntentAuthOptions>
  ) => Promise<StripeIntent | null>,
  resetIntent: () => void,
}

type Options = {
  stripe: ?StripeObject,
  searchId: string,
  tripId: string,
  amount: number,
  currency: string,
  stripeSource: string,
  postPayment?: boolean,
  paymentIntentInput?: Array<any>,
}

async function doCancelIntent(indentId) {
  try {
    await cancelPaymentIntent(indentId)
  } catch (e) {
    // TODO log unexpected error
  }
}

/**
 * Data loader to create or update payment intent. By first call it creates
 * an intent, and all next calls will update the intent with given options
 * @param  {Options} options
 * @return {PaymentIntentData}
 */
export function usePaymentIntent({
  stripe,
  searchId,
  tripId,
  amount,
  currency,
  stripeSource,
  postPayment,
  paymentIntentInput,
}: Options) {
  async function loader(state) {
    const requestBody = {
      search_id: searchId,
      result_id: tripId,
      amount,
      currency,
      stripe_origin: stripeSource,
    }

    if (!state?.data || state.data?.authorizedIntent) {
      if (state.data && state.data.authorizedIntent) {
        doCancelIntent(state.data.authorizedIntent.id)
      }
      if (postPayment) {
        const intent = await createSetupIntent({})
        return ({
          amount,
          currency,
          paymentintent_id: '',
          stripe_origin: 'USD',
          client_secret: intent.setupintent_client_secret,
          customer: intent.customer,
          authorizedIntent: null,
        }: Data)
      }
      return {
        authorizedIntent: null,
        ...(await createPaymentIntent(requestBody)),
      }
    }
    if (postPayment) {
      return state.data
    }
    if (state.data) {
      return {
        authorizedIntent: null,
        ...(await updatePaymentIntent(
          state.data.paymentintent_id,
          requestBody
        )),
      }
    }
    return null
  }

  const [state, actions] = useDataLoader<Data>(loader, [
    searchId,
    tripId,
    amount,
    currency,
    stripeSource,
    postPayment,
    ...(paymentIntentInput ?? []),
  ])

  const resetIntent = () => {
    if (state.data && state.data.paymentintent_id) {
      doCancelIntent(state.data.paymentintent_id)
    }
    actions.success(null)
    actions.retry()
  }

  const setAuthorizedIntent = (intent: StripeIntent | null) => {
    actions.success({
      ...state.data,
      authorizedIntent: intent,
    })
  }

  const authorizePaymentIntent = async (
    authArgs: $Shape<PaymentIntentAuthOptions>
  ): Promise<StripeIntent | null> => {
    const paymentIntent = state.data?.authorizedIntent
      ? state.data?.authorizedIntent
      : state.data && state.data.client_secret
      ? await createAuthorizedPaymentIntent({
          stripe,
          paymentIntentData: state.data,
          postPayment,
          ...authArgs,
        })
      : null

    setAuthorizedIntent(paymentIntent || null)
    return paymentIntent ?? null
  }

  return [
    state,
    ({
      resetIntent,
      setAuthorizedIntent,
      authorizePaymentIntent,
    }: Actions),
  ]
}
